import ky, { type Options } from 'ky'

import type { ApiEnvelope, ApiErrorPayload } from '@/lib/api-types'
import { generateIdempotencyKey } from '@/lib/idempotency'

export type FrontendCachePolicy = 'fresh-user' | 'stable-metadata'

type RequestOptions = Options & {
  cachePolicy?: FrontendCachePolicy
}

export class ApiError extends Error {
  status: number
  code: string
  details?: Record<string, unknown>

  constructor({
    status,
    error,
  }: {
    status: number
    error: ApiErrorPayload
  }) {
    super(error.message)
    this.name = 'ApiError'
    this.status = status
    this.code = error.code
    this.details = error.details
  }
}

const api = ky.create({
  hooks: {
    beforeRequest: [
      (request) => {
        request.headers.set('Accept', 'application/json')

        if (request.method !== 'GET' && !request.headers.has('Idempotency-Key')) {
          request.headers.set('Idempotency-Key', generateIdempotencyKey())
        }
      },
    ],
  },
})

function cacheModeForPolicy(policy: FrontendCachePolicy) {
  return policy === 'stable-metadata' ? 'default' : 'no-store'
}

export async function requestJson<T>(
  input: string,
  options?: RequestOptions,
): Promise<T> {
  const {
    cachePolicy = 'fresh-user',
    ...requestOptions
  } = options ?? {}
  const response = await api(input, {
    ...requestOptions,
    cache: cacheModeForPolicy(cachePolicy),
    throwHttpErrors: false,
  })

  let envelope: ApiEnvelope<T>
  try {
    envelope = (await response.json()) as ApiEnvelope<T>
  } catch {
    throw new ApiError({
      status: response.status,
      error: {
        code: response.status === 401 ? 'unauthorized' : 'parse_error',
        message:
          response.status === 401
            ? 'Authentication required'
            : `Server returned non-JSON response (${response.status})`,
      },
    })
  }

  if (!response.ok || !envelope.ok) {
    throw new ApiError({
      status: response.status,
      error: envelope.ok
        ? { code: 'internal_error', message: 'Unexpected API response' }
        : envelope.error,
    })
  }

  return envelope.data
}

export async function requestPublicJson<T>(
  input: string,
  options?: RequestOptions,
): Promise<T> {
  return requestJson<T>(input, options)
}

export async function tryRefreshAccessToken() {
  return null
}
